/**
 * LevelProgressPanel — the leveled-progress surface of "Your vocabulary"
 * (VOCAB-106 / VS-101), driven by `byLevel` + `assessedLevel` from
 * `GET /api/vocab/summary`.
 *
 * Why this exists: "493 of 12,570" is a meaningless denominator. Anchoring
 * progress to one CEFR band's core ("320 of 500 A1 words") gives a number a
 * learner can actually move.
 *
 * Three deliberate rendering rules:
 *
 * 1. **All six levels always render.** `byLevel` only carries levels that have
 *    headwords (the server GROUPs BY level), so an absent level means zero, not
 *    "unsupported" — it gets its empty state. Hiding empty bands would make the
 *    selector's shape depend on catalog state and would re-introduce exactly the
 *    level drift ADR-033 D4 just removed.
 * 2. **`byLevel` absent entirely → the panel renders nothing.** The field is
 *    `x-new-in: 3.1.0`; against an older server every band would read 0/0, which
 *    is a lie rather than a degradation. This is the graceful-degradation half
 *    of the N-1 contract.
 * 3. **The counts are presented as an approximation, never an assessment.**
 *    Levels come from a bootstrap over the seed catalog plus per-sense estimates
 *    (ADR-033 D6) — good enough to aim practice, not a placement test. Hence the
 *    footnote, and hence copy that says "words at this level", never "your
 *    level is X".
 *
 * Presentational only: the caller owns the selected band, so this component
 * stories and tests without the store.
 *
 * Spec: docs/specs/mobile/vocabulary-screen.mobile.md (VOCAB-106)
 */

import React, { useCallback } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { CEFR_LEVELS, translateCEFRLevel } from '../../utils/cefr';
import type { CEFRLevel, VocabLevelCounts, VocabSummaryResponse } from '../../generated/api/http-contracts';

/** `byLevel` as the contract hands it over: every band optional. */
export type VocabByLevel = NonNullable<VocabSummaryResponse['byLevel']>;

const ZERO_COUNTS: VocabLevelCounts = { total: 0, mastered: 0, learning: 0, new: 0, due: 0 };

/** An absent band means "no headwords here", which is zeros — not "no data". */
export function countsForLevel(byLevel: VocabByLevel | undefined, level: CEFRLevel): VocabLevelCounts {
  return byLevel?.[level] ?? ZERO_COUNTS;
}

/**
 * The band to show before the learner picks one: their assessed level, or A1
 * when `UserLearnerProfile.assessedLevel` is null (un-assessed users start at
 * the bottom rather than at whichever band happens to hold data).
 */
export function defaultLevel(assessedLevel: CEFRLevel | undefined): CEFRLevel {
  return assessedLevel ?? 'A1';
}

// ── Level pill ────────────────────────────────────────────────────

const LevelPill = React.memo(function LevelPill({
  level,
  selected,
  isAssessed,
  onPress,
}: {
  level: CEFRLevel;
  selected: boolean;
  isAssessed: boolean;
  onPress: (level: CEFRLevel) => void;
}) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const handlePress = useCallback(() => onPress(level), [onPress, level]);

  // The assessed band is called out in the label, not only by the dot, so the
  // marker is not colour/shape-only information.
  const label = isAssessed
    ? `${level} ${translateCEFRLevel(t, level)}, ${t('vocabulary.levels.assessedTag')}`
    : `${level} ${translateCEFRLevel(t, level)}`;

  return (
    <TouchableOpacity
      testID={`vocabulary-level-${level}`}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected }}
      onPress={handlePress}
      activeOpacity={0.7}
      style={[
        styles.pill,
        {
          backgroundColor: selected ? theme.primary : 'transparent',
        },
      ]}
    >
      <Text
        allowFontScaling={false}
        style={{
          fontSize: 13 * fontScale,
          fontWeight: '600',
          color: selected ? '#fff' : theme.text,
          ...ffs,
        }}
      >
        {level}
      </Text>
      {isAssessed && (
        <View
          style={[
            styles.assessedDot,
            { backgroundColor: selected ? '#fff' : theme.primary },
          ]}
        />
      )}
    </TouchableOpacity>
  );
});

// ── Two-segment progress bar ──────────────────────────────────────

const LevelBar = React.memo(function LevelBar({ counts }: { counts: VocabLevelCounts }) {
  const { theme } = useDisplayTheme();
  const { total, mastered, learning } = counts;
  const pct = (n: number) => (total > 0 ? Math.min(100, (n / total) * 100) : 0);

  return (
    <View
      style={[styles.track, { backgroundColor: theme.isDark ? '#374151' : '#E5E7EB' }]}
    >
      <View style={{ width: `${pct(mastered)}%`, height: '100%', backgroundColor: theme.success }} />
      <View style={{ width: `${pct(learning)}%`, height: '100%', backgroundColor: theme.primary }} />
    </View>
  );
});

// ── Panel ─────────────────────────────────────────────────────────

interface LevelProgressPanelProps {
  byLevel: VocabByLevel | undefined;
  assessedLevel: CEFRLevel | undefined;
  selectedLevel: CEFRLevel;
  onSelectLevel: (level: CEFRLevel) => void;
}

export const LevelProgressPanel = React.memo(function LevelProgressPanel({
  byLevel,
  assessedLevel,
  selectedLevel,
  onSelectLevel,
}: LevelProgressPanelProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  // Rule 2: no leveled data from this server — say nothing rather than zero.
  if (!byLevel) return null;

  const counts = countsForLevel(byLevel, selectedLevel);
  const isAssessed = selectedLevel === defaultLevel(assessedLevel);

  return (
    <View testID="vocabulary-levels" style={styles.section}>
      <Text
        allowFontScaling={false}
        accessibilityRole="header"
        style={{ fontSize: 18 * fontScale, fontWeight: '700', color: theme.text, ...ffs }}
      >
        {t('vocabulary.levels.title')}
      </Text>

      <View style={[styles.pillRow, { backgroundColor: theme.isDark ? '#1E293B' : '#F1F5F9' }]}>
        {CEFR_LEVELS.map((level) => (
          <LevelPill
            key={level}
            level={level}
            selected={level === selectedLevel}
            isAssessed={level === defaultLevel(assessedLevel)}
            onPress={onSelectLevel}
          />
        ))}
      </View>

      <View
        testID="vocabulary-level-panel"
        style={[styles.panel, { backgroundColor: theme.card, borderColor: theme.border }]}
      >
        <View style={styles.panelHeader}>
          <Text
            allowFontScaling={false}
            style={{ fontSize: 15 * fontScale, fontWeight: '700', color: theme.text, ...ffs }}
          >
            {selectedLevel} · {translateCEFRLevel(t, selectedLevel)}
          </Text>
          {isAssessed && (
            <View style={[styles.assessedTag, { backgroundColor: theme.primarySoft }]}>
              <Text
                allowFontScaling={false}
                style={{ fontSize: 11 * fontScale, fontWeight: '600', color: theme.primary, ...ffs }}
              >
                {t('vocabulary.levels.assessedTag')}
              </Text>
            </View>
          )}
        </View>

        {counts.total === 0 ? (
          <Text
            allowFontScaling={false}
            testID="vocabulary-level-empty"
            style={{ fontSize: 14 * fontScale, color: theme.textSecondary, marginTop: 8, ...ffs }}
          >
            {t('vocabulary.levels.empty', { level: selectedLevel })}
          </Text>
        ) : (
          <View
            testID="vocabulary-level-progress"
            accessibilityRole="progressbar"
            accessibilityLabel={t('vocabulary.levels.progress', {
              mastered: counts.mastered,
              total: counts.total,
            })}
            accessibilityValue={{ min: 0, max: counts.total, now: counts.mastered }}
          >
            <Text
              allowFontScaling={false}
              style={{ fontSize: 22 * fontScale, fontWeight: '700', color: theme.text, marginTop: 6, ...ffs }}
            >
              {t('vocabulary.levels.progress', { mastered: counts.mastered, total: counts.total })}
            </Text>
            <LevelBar counts={counts} />
            <Text
              allowFontScaling={false}
              style={{ fontSize: 13 * fontScale, color: theme.textSecondary, ...ffs }}
            >
              {t('vocabulary.levels.breakdown', {
                mastered: counts.mastered,
                learning: counts.learning,
                fresh: counts.new,
              })}
            </Text>
            {counts.due > 0 && (
              <Text
                allowFontScaling={false}
                testID="vocabulary-level-due"
                style={{ fontSize: 13 * fontScale, color: theme.primary, fontWeight: '600', marginTop: 4, ...ffs }}
              >
                {t('vocabulary.levels.due', { due: counts.due })}
              </Text>
            )}
          </View>
        )}
      </View>

      {/* Rule 3: an aim, not an assessment. */}
      <Text
        allowFontScaling={false}
        testID="vocabulary-levels-note"
        style={{ fontSize: 12 * fontScale, color: theme.textSecondary, marginTop: 8, ...ffs }}
      >
        {t('vocabulary.levels.note')}
      </Text>
    </View>
  );
});

const styles = StyleSheet.create({
  section: { marginBottom: 16 },
  pillRow: {
    flexDirection: 'row',
    borderRadius: 10,
    padding: 3,
    marginTop: 8,
  },
  pill: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 8,
    borderRadius: 8,
  },
  assessedDot: { width: 5, height: 5, borderRadius: 3 },
  panel: {
    marginTop: 8,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  panelHeader: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  assessedTag: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  track: {
    flexDirection: 'row',
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
    marginVertical: 8,
  },
});
